import * as React from 'react';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter, RouterHistory} from 'react-router-dom';

import type {FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {PageControlBarTabs} from 'shells/control-bar/page-control-bar-tabs';
import {ErrorState} from 'shells/error-state/error-state';
// @ts-expect-error - imported from .js/.jsx file
import {EnvelopeIcon} from 'shells/icon/svg/envelope-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ReportsIcon} from 'shells/icon/svg/reports-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';

import type {
    EditionStubFragment as Edition,
    NewsletterFragment as CampaignDetails,
    NewsletterStubFragment as NewsletterStub,
    CampaignUpdateInput,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {PageContainer, PageTitleBar} from '../../layout';

import {EditionListQuery} from '../graphql-wrappers/edition/edition-list-query';
import {EditionMutations} from '../graphql-wrappers/edition/edition-mutations';
import {NewsletterMutations} from '../graphql-wrappers/newsletter/newsletter-mutations';
import {NewsletterQuery} from '../graphql-wrappers/newsletter/newsletter-query';
import {MarketingStatsBanner} from '../marketing-stats-banner/marketing-stats-banner';

import {NewsletterEngagement} from './engagement/newsletter-engagement';
import {NewNewsletterModal} from './new-newsletter-modal';
import {NewsletterEditions} from './newsletter-editions';

type OwnProps = ContextRouter & {
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: any;
    getUpdatedUrl: (arg1: any) => string;
    onUpdateFilters: (arg1: FilterObject) => void;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    history: RouterHistory;
    editions: Edition[];
    campaign: CampaignDetails | null | undefined;
    campaignStub: NewsletterStub | null | undefined;
    onCreateEdition: (name: string, campaignId: string) => Promise<any>;
    onDuplicateEdition: (editionId: string, campaignId: string) => Promise<any>;
    onDeleteEdition: (editionId: string, campaignId: string) => Promise<any>;
    onRestoreEdition: (editionId: string, campaignId: string) => Promise<any>;
    onNewsletterUpdate: (input: CampaignUpdateInput) => Promise<any>;
    editionsListError: string | null | undefined;
    campaignDetailsError: string | null | undefined;
    isLoading: boolean;
    activeTab: string;
};

export function NewsletterPageComponent(props: Props) {
    const [isAddingNewEdition, setIsAddingNewEdition] = React.useState<boolean>(false);

    const newsletterId: string = props.match.params.newsletterId;

    const campaignData = props.campaign || props.campaignStub;

    const activeTab = props.activeTab;
    const title = campaignData ? campaignData.name : 'Newsletter';
    const campaignId = campaignData ? campaignData.id : '';

    const tabOptions = [
        {
            id: 'editions',
            label: 'Editions',
            icon: EnvelopeIcon,
            href: generatePath(props.match.path, {
                newsletterId: newsletterId,
                tab: 'editions',
            }),
            component: (
                <NewsletterEditions
                    {...props}
                    setIsAddingNewEdition={setIsAddingNewEdition}
                    onDeleteEdition={props.onDeleteEdition}
                    onDuplicateEdition={props.onDuplicateEdition}
                />
            ),
        },
        {
            id: 'engagement',
            label: 'Engagement',
            icon: ReportsIcon,
            href: generatePath(props.match.path, {
                newsletterId: newsletterId,
                tab: 'engagement',
            }),
            component: <NewsletterEngagement campaignId={campaignId} />,
        },
    ];

    if (
        (props.campaign &&
            props.campaign.archivedEditionCount &&
            props.campaign.archivedEditionCount > 0) ||
        activeTab === 'archived'
    ) {
        tabOptions.push({
            id: 'archived',
            component: (
                <NewsletterEditions
                    {...props}
                    setIsAddingNewEdition={setIsAddingNewEdition}
                    onRestoreEdition={props.onRestoreEdition}
                    isViewingArchived={true}
                />
            ),
            // @ts-expect-error
            isHidden: true,
        });
    }

    return (
        <>
            <PageContainer
                title={title}
                pageTitleBar={
                    <PageTitleBar
                        title={title}
                        subheaderPrimaryText={`${title} editions`}
                        editInPlaceProps={
                            campaignData
                                ? {
                                      onChange: (newValue: string | null | undefined) => {
                                          return props.onNewsletterUpdate({
                                              id: campaignData.id,
                                              name: newValue,
                                          });
                                      },
                                      placeholder: 'Name this newsletter',
                                  }
                                : undefined
                        }
                        breadcrumbs={[
                            {
                                to:
                                    campaignData && campaignData.deletedTime
                                        ? '/email-marketing/newsletters/archived'
                                        : '/email-marketing/newsletters',
                                text: 'Newsletters',
                            },
                        ]}
                        buttons={
                            <Button
                                size='big'
                                variant='primary'
                                onClick={() => {
                                    setIsAddingNewEdition(true);
                                }}
                            >
                                Create new edition
                            </Button>
                        }
                    />
                }
                controlBar={
                    <ListPageControlBar
                        archiveTab={{
                            isActive: activeTab === 'archived',
                            onChange: () => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                activeTab !== 'archived'
                                    ? appHistory.push(
                                          generatePath(props.match.path, {
                                              newsletterId: props.match.params.newsletterId,
                                              tab: 'archived',
                                          })
                                      )
                                    : appHistory.push(
                                          generatePath(props.match.path, {
                                              newsletterId: props.match.params.newsletterId,
                                              tab: 'editions',
                                          })
                                      );
                            },
                        }}
                        leftSection={
                            <PageControlBarTabs
                                tabs={{
                                    selectedTab: activeTab || 'editions',
                                    options: tabOptions,
                                }}
                                shouldOnlyShowActiveTab={false}
                            />
                        }
                        noVerticalPadding={true}
                    />
                }
            >
                <MarketingStatsBanner />
                {props.campaignDetailsError && (
                    <ErrorState errorMessage={props.campaignDetailsError} />
                )}
                {props.editionsListError && <ErrorState errorMessage={props.editionsListError} />}
                {/* @ts-expect-error */}
                <TabbedContent selectedTab={activeTab} tabOptions={tabOptions} />
            </PageContainer>
            <NewNewsletterModal
                campaignId={newsletterId}
                isOpen={isAddingNewEdition}
                onClose={() => {
                    setIsAddingNewEdition(false);
                }}
                // @ts-expect-error
                onCreateEdition={props.onCreateEdition}
                onDuplicateEdition={props.onDuplicateEdition}
            />
        </>
    );
}

export function NewsletterPage(props: OwnProps) {
    // We won't be here without this param, but flow really needs
    // it to have a default value so here we go.
    const newsletterIdParam = props.match.params.newsletterId;
    const newsletterId = newsletterIdParam ? newsletterIdParam : '';
    const activeTab = props.match.params.tab || 'editions';

    return (
        <NewsletterQuery newsletterId={newsletterId}>
            {({newsletter, newsletterStub, error: campaignDetailsError, refetch}) => (
                <EditionListQuery newsletterId={newsletterId} isArchived={activeTab === 'archived'}>
                    {({editions, isLoading, error}) => (
                        <EditionMutations>
                            {({onCreate, onDuplicate, onDelete, onRestore}) => (
                                <NewsletterMutations>
                                    {({onUpdate}) => (
                                        <NewsletterPageComponent
                                            {...props}
                                            // @ts-expect-error
                                            onCreateEdition={(name, campaignId) =>
                                                onCreate(name, campaignId).then((newEdition) => {
                                                    if (newEdition)
                                                        appHistory.push(newEdition.htmlUrlPath);
                                                })
                                            }
                                            // @ts-expect-error
                                            onDeleteEdition={(editionId, campaignId) => {
                                                return onDelete(editionId, campaignId).then(() => {
                                                    // Need to refetch to update the count of archived editions
                                                    refetch();
                                                });
                                            }}
                                            // @ts-expect-error
                                            onRestoreEdition={(editionId, campaignId) => {
                                                return onRestore(editionId, campaignId).then(() => {
                                                    // Need to refetch to update the count of archived editions
                                                    refetch();
                                                });
                                            }}
                                            // @ts-expect-error
                                            onDuplicateEdition={(editionId, campaignId, name) =>
                                                onDuplicate(editionId, campaignId, name).then(
                                                    (newEdition) => {
                                                        if (newEdition)
                                                            appHistory.push(newEdition.htmlUrlPath);
                                                    }
                                                )
                                            }
                                            onNewsletterUpdate={(input: CampaignUpdateInput) => {
                                                return onUpdate(input);
                                            }}
                                            editions={editions}
                                            editionsListError={error}
                                            campaignDetailsError={campaignDetailsError}
                                            campaign={newsletter}
                                            campaignStub={newsletterStub}
                                            isLoading={isLoading}
                                            activeTab={activeTab}
                                        />
                                    )}
                                </NewsletterMutations>
                            )}
                        </EditionMutations>
                    )}
                </EditionListQuery>
            )}
        </NewsletterQuery>
    );
}
